import React, { useState } from 'react';
import { Cog6ToothIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import ServiceNowIntegrationConfig from '../../pages/servicenow-integration/ServiceNowIntegrationConfig';
import ServiceNowSyncLogList from '../../pages/servicenow-integration/ServiceNowSyncLogList';
import ServalIntegrationConfig from '../../pages/serval-integration/ServalIntegrationConfig';
import ServalSyncLogList from '../../pages/serval-integration/ServalSyncLogList';

interface EngagementMiscTabProps {
  customerId: string;
  engagementId: string;
  customerName: string;
  engagementNumber: string;
  engagementName: string;
}

type MiscSubTab = 'servicenow-config' | 'servicenow-logs' | 'serval-config' | 'serval-logs';

const EngagementMiscTab: React.FC<EngagementMiscTabProps> = ({
  customerId,
  engagementId,
  customerName,
  engagementNumber,
  engagementName,
}) => {
  const { canAccessMenuItem, loading } = useUserPermissions();
  const [subTab, setSubTab] = useState<MiscSubTab>('servicenow-config');
  const engagementLabel = `${engagementNumber} - ${engagementName}`;

  const subTabs: Array<{ id: MiscSubTab; name: string; icon: React.ComponentType<{ className?: string }> }> = [
    { id: 'servicenow-config', name: 'ServiceNow Configuration', icon: Cog6ToothIcon },
    { id: 'servicenow-logs', name: 'ServiceNow Logs', icon: ClipboardDocumentListIcon },
    { id: 'serval-config', name: 'Serval Configuration', icon: Cog6ToothIcon },
    { id: 'serval-logs', name: 'Serval Logs', icon: ClipboardDocumentListIcon },
  ];

  if (loading) {
    return (
      <div className="flex min-h-[20vh] items-center justify-center p-8">
        <div
          className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-blue-600"
          role="status"
          aria-label="Loading"
        />
      </div>
    );
  }

  if (!canAccessMenuItem('integration_logs')) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
        <p className="text-sm text-yellow-800">
          You do not have permission to view ServiceNow integration settings.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-6">
          {subTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setSubTab(tab.id)}
              className={`${
                subTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <tab.icon className="mr-2 h-4 w-4" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {subTab === 'servicenow-config' && (
        <ServiceNowIntegrationConfig
          embedded
          customerId={customerId}
          engagementId={engagementId}
          customerName={customerName}
          engagementLabel={engagementLabel}
        />
      )}

      {subTab === 'servicenow-logs' && (
        <ServiceNowSyncLogList embedded customerId={customerId} />
      )}

      {subTab === 'serval-config' && (
        <ServalIntegrationConfig
          customerId={customerId}
          engagementId={engagementId}
          customerName={customerName}
          engagementLabel={engagementLabel}
        />
      )}

      {subTab === 'serval-logs' && (
        <ServalSyncLogList embedded customerId={customerId} />
      )}
    </div>
  );
};

export default EngagementMiscTab;
